<template>
    <ResponsiveDialog
        v-model="internalValue"
        :max-width="420"
        color="warning"
        :z-index="1300"
        :card-color="skill ? getSolidTint(colors[skill.type.id], $vuetify.theme.dark) : ''"
        :full-height="false"
        card-class="skin-card"
    >
        <template v-if="skill">
            <!-- Text/ikonky v celem popupu se ridi barvou typu skillu (TYPE_DARK_THEME), stejne
                 jako typovy chip - jednotne, at nevznika mix cerne/bile v jednom popupu. -->
            <v-card-title class="justify-center skill-detail__title" :class="textColors[skill.type.id] ? 'white--text' : 'black--text'">
                <v-icon class="mr-2" :color="textColors[skill.type.id] ? 'white' : 'black'">{{ isFirst ? 'mdi-arrow-down-circle' : 'mdi-arrow-down-drop-circle-outline' }}</v-icon>
                {{ skill.name }}
            </v-card-title>
            <v-card-text class="skill-detail__body" :class="textColors[skill.type.id] ? 'white--text' : 'black--text'">
                <div class="skill-detail__row">
                    <span class="skill-detail__label">{{ $t('pokedex.common.typeLabel') }}</span>
                    <TypePill :type="skill.type" size="small">{{ $t('common.typeNames.' + skill.type.name) }}</TypePill>
                </div>
                <div class="skill-detail__row">
                    <span class="skill-detail__label">{{ $t('pokedex.common.useLabel') }}</span>
                    <span><v-icon size="18" class="mr-1" :color="textColors[skill.type.id] ? 'white' : 'black'">{{ useIcon(skill) }}</v-icon>{{ useLabelText }}</span>
                </div>
                <!-- Vysvetleni blok/reflekt mechaniky - nahrazuje genericky "Effect" radek
                     nize, ktery by u defence skillu ukazal jen procento bez mechaniky. -->
                <!-- `$t` se NESMI predavat jako holy odkaz (`getDefenceExplanation(skill, $t)`) -
                     tim se ztrati jeho `this` binding na komponentu a vue-i18n uvnitr spadne na
                     "Cannot read properties of undefined (reading '$i18n')", cimz shodi render
                     popupu jen pro skilly s defence/STUN efektem (defence skilly nejdou rozkliknout).
                     Sipkova funkce zavolana primo tady zustava uvnitr with(this) scope, takze $t
                     ma spravny this. -->
                <div v-if="skill.defence" class="skill-detail__explain-hint">
                    <div class="skill-detail__explain-hint-label">{{ $t('pokedex.skillDetail.howItWorks') }}</div>
                    <div>{{ getDefenceExplanation(skill, (...args) => $t(...args)) }}</div>
                </div>
                <div class="skill-detail__row">
                    <span class="skill-detail__label">{{ $t('pokedex.skillDetail.damageLabel') }}</span>
                    <strong>{{ skill.minDmg }} - {{ skill.maxDmg }}</strong>
                </div>
                <div class="skill-detail__row">
                    <span class="skill-detail__label">{{ $t('pokedex.skillDetail.formulaLabel') }}</span>
                    <span>{{ skill.attackFormula }}</span>
                </div>
                <div class="skill-detail__row">
                    <span class="skill-detail__label">{{ $t('pokedex.skillDetail.bonusDmgLabel') }}</span>
                    <span>{{ skill.dmgFormula }}</span>
                </div>
                <div class="skill-detail__row" v-if="skill.effect && !skill.defence">
                    <span class="skill-detail__label">{{ $t('pokedex.common.effectLabel') }}</span>
                    <span>{{ getEffectFormula(skill) }}</span>
                </div>
                <!-- Stejny "how it works" vzor jako defence hint vyse, jen pro STUN efekt. -->
                <div v-if="skill.effect === 'STUN'" class="skill-detail__explain-hint">
                    <div class="skill-detail__explain-hint-label">{{ $t('pokedex.skillDetail.howItWorks') }}</div>
                    <div>{{ getStunExplanation(skill, (...args) => $t(...args)) }}</div>
                </div>
                <div class="skill-detail__row">
                    <span class="skill-detail__label">{{ $t('pokedex.common.areaEffectLabel') }}</span>
                    <v-icon v-if="skill.spread" size="18" :color="textColors[skill.type.id] ? 'white' : 'black'">mdi-earth</v-icon>
                    <span v-else>-</span>
                </div>
                <div class="skill-detail__row">
                    <span class="skill-detail__label">{{ $t('pokedex.skillDetail.costExpLabel') }}</span>
                    <span>{{ skill.cost }}</span>
                </div>
                <div class="skill-detail__row">
                    <span class="skill-detail__label">{{ $t('pokedex.skillDetail.levelLabel') }}</span>
                    <span>{{ skill.lvl }}</span>
                </div>
                <!-- "Znas prekazku" hlaska - jen kdyz hrac uz ma odznak trenera, ktery mu
                     prozradil, ze tenhle skill na dany obstacle typ plati
                     (Trainer::getKnownObstacleTypes()). Postupne pribyva s dalsimi odznaky. -->
                <div v-if="knownBreaks.length > 0" class="skill-detail__obstacle-hint">
                    <div class="skill-detail__obstacle-hint-label">{{ $t('pokedex.skillDetail.knownBreaksLabel') }}</div>
                    <!-- Miniatury (stejne obrazky jako ObstacleDetail.vue v souboji), ne textovy
                         vycet - jmeno jako caption pod obrazkem, ne title tooltip (na dotyku se
                         hover stejne neukaze). -->
                    <div class="skill-detail__obstacle-thumbs">
                        <div v-for="ot in knownBreaks" :key="ot.id" class="skill-detail__obstacle-thumb">
                            <v-img :src="require(`@/assets/pic/obstacles/${ot.name}.png`)" :aspect-ratio="1" class="skill-detail__obstacle-thumb-img" cover/>
                            <div class="skill-detail__obstacle-thumb-label">{{ ot.name }}</div>
                        </div>
                    </div>
                </div>
                <!-- prazdny pruh o vysce plovouciho krizku (ResponsiveDialog, mobil) - krizek je
                     tak soucasti popupu (posouva jeho vysku), ne prekryv posledniho radku -->
                <div class="skill-detail__close-spacer"/>
            </v-card-text>
        </template>
    </ResponsiveDialog>
</template>

<script>
    import {TYPE_COLORS, TYPE_DARK_THEME} from "@/utils/typeColorEnum";
    import {useIcon, useLabel, getEffectFormula, getDefenceExplanation, getStunExplanation, getSolidTint, getKnownObstacleBreaks} from "@/utils/skillDisplay";
    import ResponsiveDialog from "@/components/utils/ResponsiveDialog";
    import TypePill from "@/components/utils/TypePill";

    export default {
        name: 'SkillDetailPopup',
        components: {ResponsiveDialog, TypePill},
        props: {
            value: {type: Boolean, default: false},
            skill: {type: Object, default: null},
            // Trainer::getKnownObstacleTypes() - volitelne, default [] = zadna hlaska tam, kde
            // ho volajici nema po ruce.
            knownObstacleTypes: {type: Array, default: () => []},
        },
        data() {
            return {
                colors: TYPE_COLORS,
                textColors: TYPE_DARK_THEME,
            }
        },
        computed: {
            internalValue: {
                get() {
                    return this.value;
                },
                set(v) {
                    this.$emit('input', v);
                },
            },
            isFirst() {
                return this.skill && this.skill.preSkillIds.length === 0;
            },
            knownBreaks() {
                return getKnownObstacleBreaks(this.skill, this.knownObstacleTypes);
            },
            // useLabel() (skillDisplay.js) vraci nelokalizovanou anglickou konstantu
            // (Attack/Dodge/Shield) - preklad se resi tady lokalne, aby se nemusel
            // upravovat sdileny utils soubor mimo rozsah teto migrace.
            useLabelText() {
                if (!this.skill) return '';
                const map = {
                    Attack: this.$t('pokedex.common.useAttack'),
                    Dodge: this.$t('pokedex.common.useDodge'),
                    Shield: this.$t('pokedex.common.useShield'),
                };
                return map[useLabel(this.skill)] || useLabel(this.skill);
            },
        },
        methods: {
            useIcon,
            useLabel,
            getEffectFormula,
            getDefenceExplanation,
            getStunExplanation,
            getSolidTint,
        },
    }
</script>

<style scoped>
    /* Ikonky maji explicitni :color prop - Vuetify v-icon bez neho muze zdedit vlastni ambient
       theme barvu (dimmed) misto currentColor od rodice, stejny gotcha jako SkillPickerDialog.vue. */
    .skill-detail__row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 6px 0;
        border-bottom: 1px solid rgba(0, 0, 0, .08);
    }

    /* opacity (ne pevna rgba), at funguje pro obe varianty rodice (black--text i white--text). */
    .skill-detail__label {
        font-weight: 600;
        opacity: .65;
    }

    .skill-detail__close-spacer {
        /* Stejna hodnota jako ostatni "rezerva pro zaviraci krizek" mista v appce. */
        height: var(--responsive-dialog-close-clearance);
    }

    .skill-detail__obstacle-hint {
        margin-top: 10px;
        padding: 8px 10px;
        border-radius: 8px;
        background-color: rgba(0, 0, 0, .06);
        font-size: 14px;
    }

    .skill-detail__obstacle-hint-label {
        margin-bottom: 8px;
    }

    /* Stejny vzor jako .skill-detail__obstacle-hint vyse, jen bez top marginu (stoji hned pod
       "Use" radkem). */
    .skill-detail__explain-hint {
        margin: 4px 0 2px;
        padding: 8px 10px;
        border-radius: 8px;
        background-color: rgba(0, 0, 0, .06);
        font-size: 13px;
        line-height: 1.4;
        /* Popup se v boji vykresluje jako potomek .fight-report-card, ktera muze mit Vuetify
           text-no-wrap - white-space se dedi, reset tady zajisti zalamovani dlouhych vet. */
        white-space: normal;
        overflow-wrap: break-word;
    }

    .skill-detail__explain-hint-label {
        font-weight: 700;
        margin-bottom: 4px;
        opacity: .75;
    }

    .skill-detail__obstacle-thumbs {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
    }

    .skill-detail__obstacle-thumb {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 56px;
        flex: none;
    }

    .skill-detail__obstacle-thumb-img {
        width: 56px;
        height: 56px;
        border-radius: 10px;
        box-shadow: 1px 2px 5px rgba(0, 0, 0, .3);
    }

    .skill-detail__obstacle-thumb-label {
        margin-top: 4px;
        font-size: 11px;
        font-weight: 600;
        text-align: center;
        line-height: 1.2;
    }
</style>
